import { BooMark } from "@/components/boo/BooMark";
import { DepthPicker } from "@/components/grants/DepthPicker";
import { DEFAULT_DEPTH, type DepthChoice, depthEmpty, depthScopes, needsApproval } from "@/lib/grant-scopes";
import { ScopeChips } from "@/components/grants/GrantBits";
import { Block, NoteField, SheetShell } from "@/components/grants/SheetShell";
import { useInvalidateLink } from "@/components/grants/useGrantActions";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, type BooLinkRow, type ContainerRow } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

// Asking another Boo for access, OAuth-style: pick the Boo, pick how deep,
// say why. The other Boo's admins then approve all of it or part of it. On a
// Boo this one already learns from, it asks for more.
export function RequestAccessSheet({
  consumer,
  candidates,
  initialSourceId,
  find,
  onClose,
}: {
  consumer: { id: string; name: string };
  candidates: ContainerRow[];
  initialSourceId?: string | null;
  find?: string | null;
  onClose: () => void;
}) {
  const linksQuery = useQuery({
    queryKey: ["connections", consumer.id],
    queryFn: () => api.containers.connections.list(consumer.id),
  });
  const links: BooLinkRow[] = linksQuery.data?.using ?? [];
  const others = useMemo(
    () => candidates.filter((b) => b.id !== consumer.id && b.agentType === "boo-brain" && b.status !== "deleted"),
    [candidates, consumer.id],
  );
  const [query, setQuery] = useState(find ?? "");
  const [sourceId, setSourceId] = useState<string | null>(() => {
    if (initialSourceId && others.some((b) => b.id === initialSourceId)) return initialSourceId;
    const matches = find ? others.filter((b) => b.name.toLowerCase().includes(find.toLowerCase())) : [];
    return matches.length === 1 ? matches[0].id : null;
  });
  const [depth, setDepth] = useState<DepthChoice>(DEFAULT_DEPTH);
  // Once a Boo is picked, the list folds down to it so the rest of the form is in view.
  const [choosing, setChoosing] = useState(false);
  const [note, setNote] = useState("");
  const invalidate = useInvalidateLink();

  const source = others.find((b) => b.id === sourceId);
  const existing = links.find((l) => l.source?.id === sourceId && l.status !== "revoked");
  const asMore = existing?.status === "approved";
  const waiting = !!existing && needsApproval(existing);
  const adminOfSource = source?._permissions?.canLink === true;
  const shown = others.filter((b) => !query.trim() || b.name.toLowerCase().includes(query.trim().toLowerCase()));

  const send = useMutation({
    mutationFn: () =>
      api.containers.connections.request(consumer.id, {
        sourceId: sourceId!,
        scopes: depth.depth === "custom" ? depthScopes(depth) : { preset: depth.depth },
        note: note.trim() || null,
      }),
    onSuccess: (res) => {
      if (res.kind === "none") toast.info(`${consumer.name} already has that from ${source?.name}.`);
      else if (!res.link.waiting) toast.success(`${consumer.name} will start learning from ${source?.name} in a moment.`);
      else toast.success(`Request sent. The admins of ${source?.name} will review it, and we'll email you when they do.`);
      onClose();
    },
    onSettled: () => invalidate({ sourceId: sourceId ?? undefined, consumerId: consumer.id }),
  });

  return (
    <SheetShell
      title={asMore ? `Ask ${source?.name ?? "a Boo"} for more` : "Learn from another Boo"}
      onClose={onClose}
      busy={send.isPending}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={send.isPending}>
            Cancel
          </Button>
          <Button disabled={!source || waiting || depthEmpty(depth)} loading={send.isPending} onClick={() => send.mutate()}>
            {adminOfSource ? (asMore ? "Add to what it learns" : "Start learning") : asMore ? "Ask for more" : "Send request"}
          </Button>
        </>
      }
    >
      <Block
        title="From which Boo"
        action={
          source && !choosing ? (
            <Button variant="ghost" size="sm" onClick={() => setChoosing(true)} disabled={send.isPending}>
              Change
            </Button>
          ) : undefined
        }
      >
        {source && !choosing ? (
          <div className="flex items-center gap-3 rounded-lg border border-foreground bg-muted/60 px-3 py-2 text-sm">
            <BooMark id={source.id} className="size-6" muted={source.deploymentStatus !== "running"} />
            <span className="min-w-0 flex-1 font-medium [overflow-wrap:anywhere]">{source.name}</span>
          </div>
        ) : (
          <>
{others.length > 6 && (
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a Boo" className="pl-8" aria-label="Find a Boo" />
          </div>
        )}
        {others.length === 0 ? (
          <p className="text-sm text-muted-foreground">No other Boos yet.</p>
        ) : (
          <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Boo">
            {shown.map((boo) => {
              const link = links.find((l) => l.source?.id === boo.id && l.status !== "revoked");
              const pending = !!link && needsApproval(link);
              const active = boo.id === sourceId;
              return (
                <button
                  key={boo.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => {
                    setSourceId(boo.id);
                    setChoosing(false);
                  }}
                  className={cn(
                    "flex items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors active:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    active ? "border-foreground bg-muted/60" : "border-border hover:bg-muted/30",
                  )}
                >
                  <BooMark id={boo.id} className="size-6" muted={boo.deploymentStatus !== "running"} />
                  <span className="min-w-0 flex-1 font-medium [overflow-wrap:anywhere]">{boo.name}</span>
                  <span className="max-w-[40%] shrink-0 text-right text-xs text-muted-foreground">
                    {pending
                      ? "Needs approval"
                      : link?.status === "approved"
                        ? "Learns from it"
                        : boo.deploymentStatus === "stopped"
                          ? "Paused"
                          : boo._permissions?.canLink
                            ? "You manage it"
                            : ""}
                  </span>
                </button>
              );
            })}
            {shown.length === 0 && <p className="px-1 text-sm text-muted-foreground">No Boo matches “{query}”.</p>}
          </div>
        )}
          </>
        )}
      </Block>

      {source && waiting && (
        <p className="rounded-md bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
          Already asked. It needs approval from the admins of {source.name}.
        </p>
      )}

      {source && !waiting && (
        <>
          {asMore && existing?.granted && (
            <Block title="Already shared">
              <ScopeChips scopes={existing.granted} />
            </Block>
          )}
          <Block title={asMore ? "Ask for" : "How much"}>
            <DepthPicker value={depth} onChange={setDepth} disabled={send.isPending} />
          </Block>
          <NoteField
            id="request-note"
            label="Note (optional)"
            value={note}
            onChange={setNote}
            placeholder="Why it needs this"
          />
        </>
      )}
      {send.isError && <InlineError error={send.error} />}
    </SheetShell>
  );
}
